'use client';

import { useState, useRef, useEffect } from 'react';
import { useWholesalerBranding } from '@/context/WholesalerBrandingContext';
import { useRouter, useSearchParams } from 'next/navigation';
import { signIn } from 'next-auth/react';
import toast from 'react-hot-toast';
import Link from 'next/link';
import { useAppDispatch } from '@/redux/Hook';
import { userRegister, developerRegister, resellerFacebookSignup, resellerGoogleLogin, resellerGoogleCompleteSignup } from '@/redux/project/ThunkActions';
import { normalizeMobile } from '@/utils/mobile';
import { MobileCountryInput } from '@/app/shared/auth-layout/mobile-country-input';
import { createWholesaler } from '@/redux/wholesaler/ThunkAction';
import { iErrorResponse } from '@/customTypes/CommonServiceTypes';
import { userService } from '@/services/api/cresivoxServer';
import { routes } from '@/config/routes';
import { facebookLogin } from '@/utils/facebookLogin';
import { googleOAuthBridgeLogin } from '@/utils/googleOAuthBridge';
import { AuthGoogleCredentialButton, useGoogleCredentialAvailable } from '@/app/shared/auth-layout/google-credential-button';
import {
  AuthSplitLayout,
  AuthStepper,
  AuthField,
  AuthPasswordField,
  AuthButton,
  AuthOrRule,
  AuthGoogleButton,
  AuthFacebookButton,
  AuthStrengthMeter,
} from '@/app/shared/auth-layout/auth-split-layout';

const GOOGLE_AUTH_ENABLED = process.env.NEXT_PUBLIC_GOOGLE_AUTH_ENABLED === 'true';

type AccountType = 'reseller' | 'wholesaler' | 'developer';
type Step = 'type' | 'business' | 'verify' | 'welcome';

const ACCOUNT_TYPES = [
  {
    id: 'wholesaler' as AccountType,
    label: 'Build Your Market Palace',
    tag: 'Build Your Market Palace',
    desc: 'List catalogs at volume, manage bulk pricing tiers, and reach thousands of merchants.',
    Icon: GlyphWholesaler,
  },
  {
    id: 'developer' as AccountType,
    label: 'Develop For CresivoX',
    tag: 'Develop For CresivoX',
    desc: 'Ship plugins and themes to the ecosystem with full API access and a sandbox.',
    Icon: GlyphDeveloper,
  },
];

const BIZ_FIELDS: Record<AccountType, { key: string; label: string; placeholder?: string; optional?: boolean; type?: string; options?: string[] }[]> = {
  reseller: [
    { key: 'company', label: 'Store name', placeholder: 'Avery & Co.' },
    { key: 'url', label: 'Storefront URL', placeholder: 'avery.shop', optional: true },
    { key: 'region', label: 'Primary market', type: 'select', options: ['North America', 'Europe', 'United Kingdom', 'Asia–Pacific', 'Latin America', 'Middle East & Africa'] },
    { key: 'volume', label: 'Monthly orders', type: 'select', options: ['Just starting', '1–100', '100–1,000', '1,000–10,000', '10,000+'] },
  ],
  wholesaler: [
    { key: 'company', label: 'Company name', placeholder: 'Northwind Supply' },
    { key: 'catalog', label: 'Catalog size', type: 'select', options: ['Under 100 SKUs', '100–1,000 SKUs', '1,000–10,000 SKUs', '10,000+ SKUs'] },
    { key: 'region', label: 'Ships from', type: 'select', options: ['North America', 'Europe', 'United Kingdom', 'Asia–Pacific', 'Latin America', 'Middle East & Africa'] },
    { key: 'tax', label: 'Tax / VAT ID', placeholder: 'e.g. GB123456789', optional: true },
  ],
  developer: [
    { key: 'company', label: 'Developer / team name', placeholder: 'Lumen Labs' },
    { key: 'stack', label: 'Primary stack', type: 'select', options: ['JavaScript / TypeScript', 'PHP', 'Python', 'Ruby', 'Go', 'Other'] },
    { key: 'url', label: 'Website or GitHub', placeholder: 'github.com/lumenlabs', optional: true },
    { key: 'building', label: "What you're building", type: 'select', options: ['A plugin / app', 'A theme', 'A storefront integration', 'Just exploring'] },
  ],
};

const WELCOME_STEPS: Record<AccountType, string[]> = {
  reseller: ['Browse the marketplace catalog', 'Connect a storefront domain', 'Publish your first product'],
  wholesaler: ['Upload your product catalog', 'Set bulk pricing tiers', 'Invite merchants to your line'],
  developer: ['Generate your API keys', 'Spin up a sandbox store', 'Read the plugin quickstart'],
};

const STEP_META: Record<Step, { idx: string; step: number }> = {
  type:     { idx: '01 / 03', step: 0 },
  business: { idx: '02 / 03', step: 1 },
  verify:   { idx: '03 / 03', step: 2 },
  welcome:  { idx: '',        step: 3 },
};

function scorePw(pw: string): number {
  let s = 0;
  if (pw.length >= 8) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return s;
}

const TYPE_PARAM_MAP: Record<string, AccountType> = {
  supplier: 'wholesaler',
  wholesaler: 'wholesaler',
  developer: 'developer',
};

export default function RegisterScreen() {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { isWhitelabel, brandName } = useWholesalerBranding();

  const presetAccount = TYPE_PARAM_MAP[searchParams.get('type') || ''] ?? null;
  const presetMeta = presetAccount ? ACCOUNT_TYPES.find(t => t.id === presetAccount) : null;

  const [step, setStep] = useState<Step>('type');
  const [account, setAccount] = useState<AccountType | null>(presetAccount);
  const [creds, setCreds] = useState({ name: '', email: '', password: '', mobile: '' });
  const [biz, setBiz] = useState<Record<string, string>>({});
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpSent, setOtpSent] = useState(false);
  const [otpSeconds, setOtpSeconds] = useState(38);
  const [loading, setLoading] = useState(false);
  const [gSignup, setGSignup] = useState<{ signupToken: string; email: string } | null>(null);
  const [gMobile, setGMobile] = useState('');
  const [gBusy, setGBusy] = useState(false);
  const [typeErr, setTypeErr] = useState<Record<string, string>>({});
  const [bizErr, setBizErr] = useState<Record<string, string>>({});
  const [otpErr, setOtpErr] = useState('');
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    const w = window as typeof window & { fbq?: (...a: unknown[]) => void; gtag?: (...a: unknown[]) => void };
    if (w.fbq) w.fbq('track', 'ViewContent', { content_name: 'signup', content_category: 'signup', currency: 'INR', value: 2499 });
    if (w.gtag) w.gtag('event', 'view_item', { currency: 'INR', value: 2499, item_name: 'signup_page' });
  }, []);

  useEffect(() => {
    if (otpSeconds <= 0 || !otpSent) return;
    const id = setTimeout(() => setOtpSeconds(s => s - 1), 1000);
    return () => clearTimeout(id);
  }, [otpSeconds, otpSent]);

  /* -- STEP 1: type + credentials -- */
  function submitType(e: React.FormEvent) {
    e.preventDefault();
    const n: Record<string, string> = {};
    if (!account) n.account = 'Pick the account that fits you.';
    if (!creds.name.trim()) n.name = 'Tell us your name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(creds.email)) n.email = 'Enter a valid email address.';
    if (creds.password.length < 8) n.password = 'At least 8 characters.';
    if (!/^\+?[\d\s\-()]{10,15}$/.test(creds.mobile.trim())) n.mobile = 'Enter a valid phone number.';
    setTypeErr(n);
    if (Object.keys(n).length) return;
    setStep('business');
    // FB: Lead = email captured + account type chosen
    if (typeof window !== 'undefined') {
      const w = window as any;
      if (w.fbq) w.fbq('track', 'Lead', { content_name: account, currency: 'INR', value: 2499 });
      if (w.gtag) w.gtag('event', 'generate_lead', { currency: 'INR', value: 2499 });
    }
  }

  /* -- STEP 2: business details -- */
  async function submitBusiness(e: React.FormEvent) {
    e.preventDefault();
    const fields = BIZ_FIELDS[account!];
    const n: Record<string, string> = {};
    fields.forEach(f => {
      if (!f.optional && !(biz[f.key] && String(biz[f.key]).trim())) n[f.key] = 'Required.';
    });
    setBizErr(n);
    if (Object.keys(n).length) return;
    setLoading(true);
    try {
      const otpRes = await userService.sendEmailOtp(creds.email);
      // the service resolves (not throws) on HTTP errors; check the status
      if (!otpRes || !otpRes.httpStatusCode || otpRes.httpStatusCode >= 400) {
        throw new Error('otp send failed');
      }
    } catch {
      toast.error('Could not send the verification code. Please try again.');
      setLoading(false);
      return;
    }
    setLoading(false);
    setStep('verify');
    setOtpSent(true);
    setOtpSeconds(38);
    setOtp(['', '', '', '', '', '']);
  }

  /* -- OTP input -- */
  function setOtpAt(i: number, v: string) {
    v = v.replace(/\D/g, '');
    setOtpErr('');
    const next = [...otp];
    if (v.length > 1) {
      v.slice(0, 6 - i).split('').forEach((c, k) => { next[i + k] = c; });
      setOtp(next);
      const last = Math.min(i + v.length, 5);
      otpRefs.current[last]?.focus();
      return;
    }
    next[i] = v;
    setOtp(next);
    if (v && i < 5) otpRefs.current[i + 1]?.focus();
  }
  function otpKeyDown(i: number, e: React.KeyboardEvent) {
    if (e.key === 'Backspace' && !otp[i] && i > 0) otpRefs.current[i - 1]?.focus();
  }

  /* -- STEP 3: verify + register -- */
  async function submitVerify(e: React.FormEvent) {
    e.preventDefault();
    const code = otp.join('');
    if (code.length < 6) { setOtpErr('Enter all six digits.'); return; }
    setOtpErr('');
    setLoading(true);

    try {
      let response: any;
      const nameParts = creds.name.trim().split(' ');
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(' ') || firstName;

      if (account === 'reseller') {
        response = await dispatch(userRegister({
          firstName, lastName,
          email: creds.email,
          password: creds.password,
          confirmPassword: creds.password,
          mobile: creds.mobile,
          otp: code,
        } as any));
      } else if (account === 'wholesaler') {
        response = await dispatch(createWholesaler({
          firstName, lastName,
          email: creds.email,
          password: creds.password,
          confirmPassword: creds.password,
          businessName: biz.company || '',
          ownerName: creds.name,
          mobile: creds.mobile,
          otp: code,
        } as any));
      } else {
        response = await dispatch(developerRegister({
          firstName, lastName,
          email: creds.email,
          password: creds.password,
          confirmPassword: creds.password,
          mobile: creds.mobile,
          otp: code,
        } as any));
      }

      if (response?.meta?.requestStatus === 'fulfilled') {
        toast.success('Account created successfully!');
        await signIn('credentials', { email: creds.email, token: (response as any)?.payload?.data?.token, redirect: false });
        // FB + GA4: full registration complete
        if (typeof window !== 'undefined') {
          const w = window as any;
          if (w.fbq) w.fbq('track', 'CompleteRegistration', { content_name: account, currency: 'INR', value: 2499 });
          if (w.gtag) {
            w.gtag('event', 'sign_up', { method: 'email' });
            w.gtag('event', 'conversion', { send_to: 'G-3XGHWEKW6E', currency: 'INR', value: 2499 });
          }
        }
        setStep('welcome');
      } else {
        const payload = response?.payload as iErrorResponse;
        const msg = payload?.message?.error?.validationErrors?.body?.message ||
          payload?.message?.error?.message || 'Registration failed. Try again.';
        setOtpErr(msg);
      }
    } catch {
      setOtpErr('Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  }

  /* -- Google signup via popup bridge (skips business/verify steps) -- */
  // On admin.cresivox.com the one-click Google button returns an ID token directly; elsewhere we use the bridge.
  const googleCredentialOk = useGoogleCredentialAvailable();
  const handleGoogleSignup = () =>
    runGoogleSignup(async () => {
      const result = await googleOAuthBridgeLogin();
      return result ? { code: result.code } : null;
    });
  const handleGoogleCredential = (credential: string) => runGoogleSignup(async () => ({ credential }));

  async function completeGoogleSignup() {
    if (!gSignup || gBusy || !account) return;
    const mobile = normalizeMobile(gMobile.startsWith('+') ? gMobile : `+${gMobile}`);
    if (!mobile) return void toast.error('Enter a valid mobile number with its country code.');
    setGBusy(true);
    try {
      toast.loading('Creating your account...', { id: 'google-signup' });
      const role = account === 'wholesaler' ? 'WHOLESALER' : account === 'developer' ? 'DEVELOPER' : 'VENDOR';
      const response = await dispatch(
        resellerGoogleCompleteSignup({ signupToken: gSignup.signupToken, role, mobile }),
      );
      if (response.meta.requestStatus !== 'fulfilled') {
        const body = (response.payload as any)?.message;
        const apiError = body?.error;
        toast.error(apiError?.message || body?.message || 'Could not create your account.', { id: 'google-signup' });
        if (apiError?.error === 'GOOGLE_SIGNUP_EXPIRED') setGSignup(null);
        return;
      }
      toast.success('Registration via Google successful!', { id: 'google-signup' });
      const payloadData = (response.payload as any)?.data;
      await signIn('credentials', {
        email: payloadData?.user?.email || '',
        token: payloadData?.token,
        redirect: false,
      });
      if (typeof window !== 'undefined') {
        const w = window as any;
        if (w.fbq) w.fbq('track', 'CompleteRegistration', { content_name: account, currency: 'INR', value: 2499 });
        if (w.gtag) w.gtag('event', 'sign_up', { method: 'google' });
      }
      setGSignup(null);
      setStep('welcome');
    } catch {
      toast.error('An error occurred while creating your account.', { id: 'google-signup' });
    } finally {
      setGBusy(false);
    }
  }

  async function runGoogleSignup(getPayload: () => Promise<{ code?: string; credential?: string } | null>) {
    if (!account) {
      setTypeErr(e => ({ ...e, account: 'Pick an account type to continue with Google.' as any }));
      return;
    }
    try {
      const googlePayload = await getPayload();
      if (!googlePayload) {
        toast.error('Google Sign Up Failed');
        return;
      }

      toast.loading('Verifying...', { id: 'google-signup' });
      // Same flow as sign-in: an existing Google account signs in; a new one must verify a mobile number first.
      const response = await dispatch(resellerGoogleLogin({ ...googlePayload, autoSignup: true }));

      if (response?.meta?.requestStatus !== 'fulfilled') {
        const apiError = (response?.payload as any)?.message?.error;
        if (apiError?.error === 'GOOGLE_SIGNUP_DETAILS_REQUIRED' && apiError.signupToken) {
          toast.dismiss('google-signup');
          setGMobile('');
          setGSignup({ signupToken: apiError.signupToken, email: apiError.email || '' });
          return;
        }
        if (apiError?.error === 'GOOGLE_LINK_CONFIRM_REQUIRED') {
          toast.error('An account already exists for this email. Please sign in instead.', { id: 'google-signup' });
          return;
        }
      }

      if (response?.meta?.requestStatus === 'fulfilled') {
        toast.success('Registration via Google successful!', { id: 'google-signup' });
        const payloadData = (response.payload as any)?.data;

        await signIn('credentials', {
          email: payloadData?.user?.email || '',
          token: (response as any)?.payload?.data?.token,
          redirect: false,
        });

        if (typeof window !== 'undefined') {
          const w = window as any;
          if (w.fbq) w.fbq('track', 'CompleteRegistration', { content_name: account, currency: 'INR', value: 2499 });
          if (w.gtag) {
            w.gtag('event', 'sign_up', { method: 'google' });
            w.gtag('event', 'conversion', { send_to: 'G-3XGHWEKW6E', currency: 'INR', value: 2499 });
          }
        }
        setStep('welcome');
      } else {
        const payload = response?.payload as unknown as iErrorResponse;
        const msg = payload?.message?.error?.validationErrors?.body?.message ||
          payload?.message?.error?.message || (payload?.message as any)?.message || 'Could not sign up with Google.';
        toast.error(msg, { id: 'google-signup' });
      }
    } catch {
      toast.error('An error occurred during Google Sign Up.', { id: 'google-signup' });
    }
  }

  /* -- Facebook signup (skips business/verify steps) -- */
  async function handleFacebookSignup() {
    try {
      const fbResult = await facebookLogin();
      if (!fbResult) {
        toast.error('Facebook Sign Up Failed');
        return;
      }

      toast.loading('Verifying...', { id: 'facebook-signup' });
      const response = await dispatch(resellerFacebookSignup({ accessToken: fbResult.accessToken }));

      if (response?.meta?.requestStatus === 'fulfilled') {
        toast.success('Registration via Facebook successful!', { id: 'facebook-signup' });
        const payloadData = (response.payload as any)?.data;

        await signIn('credentials', {
          email: payloadData?.user?.email || '',
          token: (response as any)?.payload?.data?.token,
          redirect: false,
        });

        if (typeof window !== 'undefined') {
          const w = window as any;
          if (w.fbq) w.fbq('track', 'CompleteRegistration', { content_name: account, currency: 'INR', value: 2499 });
          if (w.gtag) {
            w.gtag('event', 'sign_up', { method: 'facebook' });
            w.gtag('event', 'conversion', { send_to: 'G-3XGHWEKW6E', currency: 'INR', value: 2499 });
          }
        }
        setStep('welcome');
      } else {
        const payload = response?.payload as unknown as iErrorResponse;
        const msg = payload?.message?.error?.validationErrors?.body?.message ||
          payload?.message?.error?.message || 'User already exists. Please sign in instead.';
        toast.error(msg, { id: 'facebook-signup' });
      }
    } catch {
      toast.error('An error occurred during Facebook Sign Up.', { id: 'facebook-signup' });
    }
  }

  const meta = ACCOUNT_TYPES.find(t => t.id === account);
  const stepNum = STEP_META[step];

  return (
    <AuthSplitLayout
      mode={step === 'welcome' ? 'done' : step === 'type' ? 'up' : 'up'}
      stepIndex={stepNum.idx}
      selectedAccount={account}
    >
      {/* STEP 1: TYPE + CREDENTIALS */}
      {step === 'type' && (
        <form onSubmit={submitType} style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <header>
            <AuthStepper steps={['Account', 'Business', 'Verify']} current={0} />
            <h1 className="auth-display" style={{ fontSize: 37, marginTop: 20 }}>
              Create your<br /><em style={{ fontStyle: 'italic' }}>account</em>.
            </h1>
            <p style={{ margin: '13px 0 0', color: 'var(--muted)', fontSize: 15, lineHeight: 1.5 }}>
              {isWhitelabel ? `Choose how you'll use ${brandName}.` : "Choose how you'll use cresivox."} You can add other roles later.
            </p>
          </header>

          {presetAccount && presetMeta ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 10, background: 'var(--paper-2)' }}>
              <span style={{
                flex: '0 0 auto', width: 34, height: 34, borderRadius: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'var(--accent)', color: 'var(--accent-ink)',
              }}><presetMeta.Icon /></span>
              <span className="auth-serif" style={{ fontSize: 15.5, color: 'var(--ink)' }}>
                Signing up for {presetMeta.label}
              </span>
            </div>
          ) : (
            <div>
              <div className="auth-kicker" style={{ marginBottom: 12 }}>I'm joining as a</div>
              <div style={{ display: 'grid', gap: 10 }}>
                {ACCOUNT_TYPES.map(t => {
                  const sel = account === t.id;
                  return (
                    <button type="button" key={t.id}
                      className={'auth-pick' + (sel ? ' sel' : '')}
                      onClick={() => { setAccount(t.id); setTypeErr(e => ({ ...e, account: undefined as any })); }}>
                      <span style={{
                        flex: '0 0 auto', width: 42, height: 42, borderRadius: 8,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: sel ? 'var(--accent)' : 'var(--paper-2)',
                        color: sel ? 'var(--accent-ink)' : 'var(--ink-2)', transition: 'all .16s ease',
                      }}><t.Icon /></span>
                      <span style={{ flex: 1 }}>
                        <span style={{ display: 'flex', alignItems: 'baseline', gap: 9 }}>
                          <span className="auth-serif" style={{ fontSize: 19, color: 'var(--ink)' }}>{t.label}</span>
                        </span>
                        <span style={{ display: 'block', marginTop: 4, fontSize: 13.5, color: 'var(--muted)', lineHeight: 1.45 }}>{t.desc}</span>
                      </span>
                      <span style={{
                        flex: '0 0 auto', width: 20, height: 20, borderRadius: '50%', marginTop: 2,
                        border: `1.5px solid ${sel ? 'var(--accent)' : 'var(--line-2)'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {sel && <span style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--accent)' }} />}
                      </span>
                    </button>
                  );
                })}
              </div>
              {typeErr.account && <div className="auth-err" style={{ marginTop: 9 }}><DotIcon />{typeErr.account}</div>}
            </div>
          )}

          <div style={{ display: 'grid', gap: 18 }}>
            <AuthField id="reg-name" label="Full name" placeholder="Jordan Avery" autoComplete="name"
              value={creds.name} error={typeErr.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCreds(c => ({ ...c, name: e.target.value }))} />
            <AuthField id="reg-email" label="Work email" type="email" placeholder="you@studio.com" autoComplete="email"
              value={creds.email} error={typeErr.email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCreds(c => ({ ...c, email: e.target.value }))} />
            <AuthField id="reg-mobile" label="Phone number" type="tel" placeholder="+91 9876543210" autoComplete="tel"
              value={creds.mobile} error={typeErr.mobile}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCreds(c => ({ ...c, mobile: e.target.value }))} />
            <div>
              <AuthPasswordField id="reg-pw" label="Password"
                hint={typeErr.password ? undefined : '8+ characters'}
                value={creds.password} error={typeErr.password} autoComplete="new-password"
                onChange={(e) => setCreds(c => ({ ...c, password: e.target.value }))} />
              <AuthStrengthMeter score={scorePw(creds.password)} show={creds.password.length > 0} />
            </div>
          </div>

          <AuthButton type="submit" loading={loading}>Continue<ArrowRight /></AuthButton>
          {/* Google signup is staged behind NEXT_PUBLIC_GOOGLE_AUTH_ENABLED (enable on staging first).
              Facebook signup stays pulled; its handler below is kept intact. */}
          {GOOGLE_AUTH_ENABLED && (
            <>
              <AuthOrRule />
              {googleCredentialOk && account ? (
                <AuthGoogleCredentialButton mode="signup" onCredential={handleGoogleCredential}
                  onError={() => toast.error('Google Sign Up Failed')} />
              ) : (
                <AuthGoogleButton onClick={handleGoogleSignup} label="Sign up with Google" />
              )}
              {gSignup && (
                <div role="alert"
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); completeGoogleSignup(); } }}
                  style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16, border: '1px solid var(--line)', borderRadius: 12, lineHeight: 1.5 }}>
                  <div style={{ fontWeight: 600 }}>Add your mobile number</div>
                  <div style={{ fontSize: 14, color: 'var(--muted)' }}>
                    Almost done for {gSignup.email}. We need your mobile number to create the account.
                  </div>
                  <MobileCountryInput id="google-reg-mobile" value={gMobile} onChange={setGMobile} />
                  <div style={{ display: 'flex', gap: 10 }}>
                    <AuthButton type="button" loading={gBusy} onClick={completeGoogleSignup}>Create account</AuthButton>
                    <button type="button" className="auth-btn auth-btn-ghost" onClick={() => setGSignup(null)} disabled={gBusy}>Cancel</button>
                  </div>
                </div>
              )}
            </>
          )}

          <p style={{ margin: 0, fontSize: 14.5, color: 'var(--muted)' }}>
            Already have an account?{' '}
            <Link href={routes.auth.signIn1} className="auth-linklike">Sign in</Link>
          </p>
        </form>
      )}

      {/* STEP 2: BUSINESS DETAILS */}
      {step === 'business' && account && (
        <form onSubmit={submitBusiness} style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <header>
            <AuthStepper steps={['Account', 'Business', 'Verify']} current={1} />
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 18 }}>
              <span style={{ width: 26, height: 26, borderRadius: 6, background: 'var(--accent)', color: 'var(--accent-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {meta && <meta.Icon />}
              </span>
              <span style={{ fontSize: 12.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--muted)' }}>{meta?.label} account</span>
            </div>
            <h1 className="auth-display" style={{ fontSize: 37, marginTop: 12 }}>
              Your <em style={{ fontStyle: 'italic' }}>business</em>.
            </h1>
            <p style={{ margin: '13px 0 0', color: 'var(--muted)', fontSize: 15, lineHeight: 1.5 }}>
              {account === 'reseller' && 'Tell us about your store so we can tailor the marketplace.'}
              {account === 'wholesaler' && 'Help merchants find your catalog with a few supply details.'}
              {account === 'developer' && 'A little about what you build so we can set up your sandbox.'}
            </p>
          </header>

          <div style={{ display: 'grid', gap: 18 }}>
            {BIZ_FIELDS[account].map(f => (
              f.type === 'select' ? (
                <div key={f.key}>
                  <label className="auth-field-label">{f.label}</label>
                  <div style={{ position: 'relative' }}>
                    <select
                      className={'auth-select' + (bizErr[f.key] ? ' has-error' : '')}
                      value={biz[f.key] || ''}
                      onChange={e => setBiz(b => ({ ...b, [f.key]: e.target.value }))}
                      style={{ color: biz[f.key] ? 'var(--ink)' : 'var(--faint)' }}
                    >
                      <option value="" disabled>Select…</option>
                      {f.options!.map(o => <option key={o} value={o} style={{ color: 'var(--ink)' }}>{o}</option>)}
                    </select>
                    <span style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--muted)' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m6 9 6 6 6-6" /></svg>
                    </span>
                  </div>
                  {bizErr[f.key] && <div className="auth-err"><DotIcon />{bizErr[f.key]}</div>}
                </div>
              ) : (
                <AuthField key={f.key} id={`biz-${f.key}`}
                  label={f.label + (f.optional ? '  (optional)' : '')}
                  placeholder={f.placeholder}
                  value={biz[f.key] || ''}
                  error={bizErr[f.key]}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBiz(b => ({ ...b, [f.key]: e.target.value }))}
                />
              )
            ))}
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <AuthButton ghost style={{ width: 'auto', paddingLeft: 16, paddingRight: 18 }} onClick={() => setStep('type')}>
              <ArrowLeft />Back
            </AuthButton>
            <AuthButton type="submit">Continue<ArrowRight /></AuthButton>
          </div>
        </form>
      )}

      {/* STEP 3: OTP VERIFY */}
      {step === 'verify' && (
        <form onSubmit={submitVerify} style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <header>
            <AuthStepper steps={['Account', 'Business', 'Verify']} current={2} />
            <div style={{ marginTop: 20, width: 52, height: 52, borderRadius: 10, border: '1px solid var(--line-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink)' }}>
              <MailIcon />
            </div>
            <h1 className="auth-display" style={{ fontSize: 36, marginTop: 16 }}>
              Check your <em style={{ fontStyle: 'italic' }}>inbox</em>.
            </h1>
            <p style={{ margin: '13px 0 0', color: 'var(--muted)', fontSize: 15, lineHeight: 1.5 }}>
              We sent a 6-digit code to <strong style={{ color: 'var(--ink)', fontWeight: 600 }}>{creds.email || 'your email'}</strong>.
              Enter it below to verify your account.
            </p>
          </header>

          <div>
            <div style={{ display: 'flex', gap: 9 }}
              onPaste={e => { const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6); if (text) { e.preventDefault(); text.split('').forEach((ch, i) => setOtpAt(i, ch)); } }}>
              {otp.map((d, i) => (
                <input key={i}
                  ref={el => { otpRefs.current[i] = el; }}
                  className={'auth-otp-cell' + (d ? ' filled' : '')}
                  inputMode="numeric" maxLength={1} value={d}
                  onChange={e => setOtpAt(i, e.target.value)}
                  onKeyDown={e => otpKeyDown(i, e)}
                  aria-label={`Digit ${i + 1}`}
                  autoFocus={i === 0}
                />
              ))}
            </div>
            {otpErr && <div className="auth-err" style={{ marginTop: 12 }}><DotIcon />{otpErr}</div>}
          </div>

          <AuthButton type="submit" loading={loading}>Verify & continue<ArrowRight /></AuthButton>

          <div style={{ fontSize: 14, color: 'var(--muted)' }}>
            Didn't get it?{' '}
            {otpSeconds > 0
              ? <span>Resend in <span className="auth-serif" style={{ color: 'var(--ink-2)' }}>{otpSeconds}s</span></span>
              : <button type="button" className="auth-linklike" onClick={() => { setOtpSeconds(38); }}>Resend code</button>}
            <span style={{ margin: '0 8px', color: 'var(--line-2)' }}>·</span>
            <button type="button" className="auth-linklike" onClick={() => setStep('type')}>Wrong email?</button>
          </div>
        </form>
      )}

      {/* WELCOME */}
      {step === 'welcome' && account && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
          <div>
            <span style={{ width: 60, height: 60, borderRadius: '50%', background: 'var(--accent)', color: 'var(--accent-ink)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckBig />
            </span>
          </div>
          <header>
            <div className="auth-kicker">You're in</div>
            <h1 className="auth-display" style={{ fontSize: 42, marginTop: 14 }}>
              Welcome,{' '}
              <em style={{ fontStyle: 'italic' }}>{creds.name.trim().split(' ')[0] || 'aboard'}</em>.
            </h1>
            <p style={{ margin: '15px 0 0', color: 'var(--muted)', fontSize: 15.5, lineHeight: 1.55 }}>
              Your <strong style={{ color: 'var(--ink)', fontWeight: 600 }}>{meta?.label}</strong> account is ready.
              Here's how to make your first move.
            </p>
          </header>

          <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', borderTop: '1px solid var(--line)' }}>
            {WELCOME_STEPS[account].map((s, i) => (
              <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 2px', borderBottom: '1px solid var(--line)' }}>
                <span className="auth-serif" style={{ fontSize: 17, color: 'var(--faint)', width: 22 }}>{String(i + 1).padStart(2, '0')}</span>
                <span style={{ flex: 1, fontSize: 15.5, color: 'var(--ink)' }}>{s}</span>
                <ArrowRight />
              </li>
            ))}
          </ol>

          <AuthButton onClick={() => router.push('/')}>Go to dashboard<ArrowRight /></AuthButton>
        </div>
      )}
    </AuthSplitLayout>
  );
}

/* ---- icons ---- */
function ArrowLeft() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M15 5l-7 7 7 7" /></svg>; }
function ArrowRight() { return <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M5 12h14M13 5l7 7-7 7" /></svg>; }
function CheckBig() { return <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 6" /></svg>; }
function MailIcon() { return <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="M4 7l8 6 8-6" /></svg>; }
function DotIcon() { return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" /></svg>; }
function GlyphReseller() { return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 7h18l-1.4 11.2a2 2 0 0 1-2 1.8H6.4a2 2 0 0 1-2-1.8L3 7Z" /><path d="M8 7V5.5A4 4 0 0 1 16 5.5V7" /></svg>; }
function GlyphWholesaler() { return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 9.5 12 4l9 5.5V19a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5Z" /><path d="M7 20v-6h4v6M14 13h3v3h-3z" /></svg>; }
function GlyphDeveloper() { return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="m8 8-4 4 4 4M16 8l4 4-4 4M13.5 5l-3 14" strokeLinecap="round" strokeLinejoin="round" /></svg>; }
